import {
  Box, Paper, Typography, useTheme, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import { defaultColDef, exactFloatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import CheckBox from "../../components/input/CheckBox";
import { leeds, leeds_flat } from "../../components/LEED_categories";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Product } from "../../db_access/product";
import { Product_EPD } from "../../db_access/product_epd";
import AgGrid from "../../components/grid/AgGrid";
import MetricCard from "../../app_components/MetricCard";
import { LuFootprints, LuSearch, LuChevronRight, LuX } from "react-icons/lu";
import EPDFiltersSidebar from "./EPDFiltersSidebar";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import "../../style/sidemenu-pages-style/EPDTable.css";
import "../../style/pages-style/products/ProductDashboard.css";
import imgConcrete from "../../assets/categories/concrete.png";
import imgMasonry from "../../assets/categories/masonry.png";
import imgMetals from "../../assets/categories/metals.png";
import imgWood from "../../assets/categories/wood.png";
import imgThermal from "../../assets/categories/thermal.png";
import imgOpenings from "../../assets/categories/openings.png";
import imgFinishings from "../../assets/categories/finishings.png";
import imgPlumbing from "../../assets/categories/plumbing.png";
import imgElectrical from "../../assets/categories/electrical.png";
import imgExterior from "../../assets/categories/exterior.png";

const CATEGORY_IMAGES = {
  "Concrete": imgConcrete,
  "Masonry": imgMasonry,
  "Metals": imgMetals,
  "Wood, Plastic & Composites": imgWood,
  "Thermal and Moisture Protection": imgThermal,
  "Openings": imgOpenings,
  "Finishings": imgFinishings,
  "Plumbing": imgPlumbing,
  "Electrical": imgElectrical,
  "Exterior Improvements": imgExterior,
};

const defaultCols = ["name", "description", "declared_unit", "gwp", "plant_geography", "verifier_org", "updated_on"];

const CATEGORY_COLORS = {
  "Concrete": { from: "#64748b", to: "#1e293b" },
  "Masonry": { from: "#c2410c", to: "#7c2d12" },
  "Metals": { from: "#1d4ed8", to: "#1e3a8a" },
  "Wood, Plastic & Composites": { from: "#15803d", to: "#14532d" },
  "Thermal and Moisture Protection": { from: "#0e7490", to: "#164e63" },
  "Openings": { from: "#7c3aed", to: "#4c1d95" },
  "Finishings": { from: "#be185d", to: "#831843" },
  "Plumbing": { from: "#0369a1", to: "#0c4a6e" },
  "Electrical": { from: "#b45309", to: "#78350f" },
  "Exterior Improvements": { from: "#4338ca", to: "#312e81" },
};

const DEFAULT_COLORS = { from: "#374151", to: "#111827" };

const REGION_MAPPING = {
  "US": ["North America"],
  "CA": ["North America"],
  "GB": ["Europe"],
  "IE": ["Europe"],
  "IT": ["Europe"],
  "DE": ["Europe"],
  "FR": ["Europe"],
  "ES": ["Europe"],
  "NL": ["Europe"],
  "TR": ["Europe", "Asia"],
  "CN": ["Asia"],
  "IN": ["Asia"],
  "AU": ["Oceania"],
  "NZ": ["Oceania"],
  "SE": ["Europe", "Nordics"],
  "NO": ["Europe", "Nordics"],
  "FI": ["Europe", "Nordics"],
  "DK": ["Europe", "Nordics"],
  "PL": ["Europe"],
  "BE": ["Europe"],
  "AT": ["Europe"],
  "CH": ["Europe"],
  "JP": ["Asia"],
  "KR": ["Asia"],
  "BR": ["South America"],
  "MX": ["South America"],
  "ZA": ["Africa"],
};

const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return "🏳️";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  try {
    return String.fromCodePoint(...codePoints);
  } catch (e) {
    return "🏳️";
  }
};

const getCountryName = (countryCode) => {
  if (!countryCode) return "";
  const code = countryCode.toUpperCase().trim();
  try {
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    const name = regionNames.of(code);
    if (name) return name;
  } catch (e) {
    // fallback
  }
  return code;
};

function EPDTable({ setGwpValues, setIsBoxplotView, calculateStandardDeviation, setFirstSelectedRow }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { columnVisibilities, enqueueAlert } = useContextProvider();
  const { t } = useTranslation();
  const gridHeaderContainerRef = useRef();
  const gridSectionRef = useRef(null);

  // ─── Draft Filter States (updated by sidebar, not used for fetching) ──────
  const [searchQuery, setSearchQuery] = useState("");
  const [allManufacturersList, setAllManufacturersList] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);

  // ─── Applied snapshot (only set when "Apply Filters" is clicked) ──────────
  const [appliedSnapshot, setAppliedSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ─── Data States ─────────────────────────────────────────────────────────
  const [epds, setEPDs] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [isBoxplotView, setIsBoxplotViewState] = useState(false);
  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [filteredEPDs, setFilteredEPDs] = useState([]);
  const [showAllManufacturersModal, setShowAllManufacturersModal] = useState(false);
  const [allMfrSearchTerm, setAllMfrSearchTerm] = useState("");

  const colVisibility_key = "epd_table_columnVisibility";
  const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);

  const colDefs = [
    { field: "id", hide: true },
    {
      headerName: t("Category"),
      field: "category",
      ...defaultColDef,
      ...leftAlignColDef,
      headerCheckboxSelection: true,
      checkboxSelection: false,
      hide: isHidden("category"),
      cellRenderer: (params) => {
        const v = params.value;
        if (v === "Unsupported") return t("Unknown");
        return leeds_flat[v] || v;
      },
    },
    { headerName: t("Manufacturer"), field: "manufacturer", ...defaultColDef, ...leftAlignColDef, hide: isHidden("manufacturer") },
    { headerName: t("Plant or Group"), field: "plant_or_group", ...defaultColDef, ...leftAlignColDef, hide: isHidden("plant_or_group") },
    { headerName: t("Name"), field: "name", ...defaultColDef, ...leftAlignColDef, hide: isHidden("name") },
    { headerName: t("Description"), field: "description", ...defaultColDef, ...leftAlignColDef, hide: isHidden("description") },
    { headerName: t("Program Operator"), field: "program_operator", ...defaultColDef, ...leftAlignColDef, hide: isHidden("program_operator") },
    { headerName: t("Verifier Org"), field: "verifier_org", ...defaultColDef, ...leftAlignColDef, hide: isHidden("verifier_org") },
    { headerName: t("Plant Geography"), field: "plant_geography", ...defaultColDef, ...leftAlignColDef, hide: isHidden("plant_geography") },
    { headerName: t("Declared Unit"), field: "declared_unit", ...defaultColDef, ...leftAlignColDef, hide: isHidden("declared_unit") },
    { headerName: t("Normalized Unit"), field: "normalized_unit", ...defaultColDef, ...leftAlignColDef, hide: isHidden("normalized_unit") },
    { headerName: t("GWP"), field: "gwp", ...exactFloatFormater, ...defaultColDef, ...rightAlignColDef, hide: isHidden("gwp") },
    { headerName: t("AP (kg SO₂e)"), field: "ap", ...exactFloatFormater, ...defaultColDef, ...rightAlignColDef, hide: isHidden("ap") },
    { headerName: t("ODP (kg CFC-11e)"), field: "odp", ...exactFloatFormater, ...defaultColDef, ...rightAlignColDef, hide: isHidden("odp") },
    { headerName: t("POCP (kg C₂H₄e)"), field: "pocp", ...exactFloatFormater, ...defaultColDef, ...rightAlignColDef, hide: isHidden("pocp") },
    { headerName: t("EP-marine (kg N)"), field: "ep_marine", ...exactFloatFormater, ...defaultColDef, ...rightAlignColDef, hide: isHidden("ep_marine") },
    { headerName: t("Updated On"), field: "updated_on", ...defaultColDef, ...rightAlignColDef, hide: isHidden("updated_on") },
  ];

  // ─── Fetch manufacturers and pre-populate Concrete EPDs on mount ──────────
  useEffect(() => {
    Product_EPD.get_manufacturers()
      .then((res) => res.json())
      .then((data) => {
        const sorted = (data || []).sort((a, b) => {
          const countA = typeof a === "object" ? (a.count || 0) : 0;
          const countB = typeof b === "object" ? (b.count || 0) : 0;
          return countB - countA;
        });
        setAllManufacturersList(sorted);
      })
      .catch((err) => console.error("Error fetching manufacturers:", err));

    // Pre-populate Concrete EPDs so filters (countries, years, units) are not empty on load
    Product_EPD.get_by_category("Concrete")
      .then((res) => res.json())
      .then((data) => setEPDs(data || []))
      .catch((err) => console.error("Error loading initial Concrete EPDs:", err));
  }, []);

  // ─── Fetch EPD data - ONLY when appliedSnapshot changes ──────────────────
  useEffect(() => {
    if (!appliedSnapshot) return;

    const { search, manufacturers, category, subCategory } = appliedSnapshot;
    setIsLoading(true);
    setEPDs([]);

    if (manufacturers.length > 0) {
      Promise.all(manufacturers.map((m) => Product_EPD.get_by_manufacturer(m).then((r) => r.json())))
        .then((results) => setEPDs(results.flat()))
        .catch((err) => console.error("Error fetching EPDs by manufacturer:", err))
        .finally(() => setIsLoading(false));
    } else if (subCategory) {
      Product_EPD.get_by_category(subCategory.key)
        .then((res) => res.json())
        .then((data) => setEPDs(data || []))
        .catch((err) => console.error("Error fetching EPDs by sub-category:", err))
        .finally(() => setIsLoading(false));
    } else if (category) {
      const subKeys = category.sub_categories?.map((s) => s.key) || [category.key];
      Promise.all(subKeys.map((catKey) => Product_EPD.get_by_category(catKey).then((r) => r.json())))
        .then((results) => setEPDs(results.flat()))
        .catch((err) => console.error("Error fetching EPDs for category:", err))
        .finally(() => setIsLoading(false));
    } else if (search && search.trim()) {
      const q = search.toLowerCase().trim();
      const matchedCatKeys = [];

      leeds.forEach((cat) => {
        if (cat.label.toLowerCase().includes(q) || cat.key.toLowerCase().includes(q)) {
          cat.sub_categories?.forEach((s) => matchedCatKeys.push(s.key));
          matchedCatKeys.push(cat.key);
        } else {
          cat.sub_categories?.forEach((sub) => {
            if (sub.label.toLowerCase().includes(q) || sub.key.toLowerCase().includes(q)) {
              matchedCatKeys.push(sub.key);
            }
          });
        }
      });

      const matchedMfgs = allManufacturersList
        .map((m) => (typeof m === "string" ? m : m.manufacturer || ""))
        .filter((mName) => mName.toLowerCase().includes(q));

      const categoryPromises = Array.from(new Set(matchedCatKeys)).map((catKey) =>
        Product_EPD.get_by_category(catKey).then((r) => r.json()).catch(() => [])
      );
      const mfgPromises = Array.from(new Set(matchedMfgs)).map((mfgName) =>
        Product_EPD.get_by_manufacturer(mfgName).then((r) => r.json()).catch(() => [])
      );

      Promise.all([...categoryPromises, ...mfgPromises])
        .then((results) => {
          const merged = results.flat();
          const seen = new Set();
          const unique = merged.filter((item) => {
            if (!item || !item.id) return false;
            const duplicate = seen.has(item.id);
            seen.add(item.id);
            return !duplicate;
          });
          setEPDs(unique);
        })
        .catch((err) => console.error("Error during search fetch:", err))
        .finally(() => setIsLoading(false));
    } else {
      // Fallback
      Product_EPD.get_by_category("Concrete")
        .then((res) => res.json())
        .then((data) => setEPDs(data || []))
        .catch(() => setEPDs([]))
        .finally(() => setIsLoading(false));
    }
  }, [appliedSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Client-side filtering using the applied snapshot ─────────────────────
  useEffect(() => {
    if (!appliedSnapshot) return;
    const { search, manufacturers, category, subCategory, unit, programs, regions, countries, standards, years } = appliedSnapshot;

    const filtered = epds.filter((item) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const mfgMatch = item.manufacturer?.toLowerCase().includes(q);

        // Find matching parent category in leeds
        const catObj = leeds.find((c) => c.key === item.category || c.sub_categories?.some((s) => s.key === item.category));
        const catMatch = catObj ? catObj.label.toLowerCase().includes(q) : false;

        let subMatch = false;
        if (catObj && catObj.sub_categories) {
          const matchedSub = catObj.sub_categories.find((s) => s.key === item.category);
          if (matchedSub) {
            subMatch = matchedSub.label.toLowerCase().includes(q);
          }
        }

        if (!mfgMatch && !catMatch && !subMatch) return false;
      }

      if (manufacturers.length > 0 && !manufacturers.includes(item.manufacturer)) return false;

      if (subCategory) {
        if (item.category !== subCategory.key) return false;
      } else if (category) {
        const subKeys = category.sub_categories?.map((s) => s.key) || [category.key];
        if (!subKeys.includes(item.category)) return false;
      }

      if (unit) {
        const targetUnit = unit.toLowerCase();
        const normUnit = (item.normalized_unit || "").toLowerCase();
        const declUnit = (item.declared_unit || "").toLowerCase();
        let unitMatched = normUnit === targetUnit || normUnit.includes(targetUnit);
        if (!unitMatched) {
          if (targetUnit === "m²" || targetUnit === "m2") {
            unitMatched = declUnit.includes("m2") || declUnit.includes("m²") || declUnit.includes("sq m") || declUnit.includes("sq.m");
          } else if (targetUnit === "m³" || targetUnit === "m3") {
            unitMatched = declUnit.includes("m3") || declUnit.includes("m³") || declUnit.includes("cu m");
          } else if (targetUnit === "kg") {
            unitMatched = declUnit.includes("kg") || declUnit.includes("kilo");
          } else if (targetUnit === "m") {
            unitMatched = declUnit.includes(" m") || declUnit === "m";
          } else if (targetUnit === "unit") {
            unitMatched = declUnit.includes("unit") || declUnit.includes("piece") || declUnit.includes("item");
          } else {
            unitMatched = declUnit.includes(targetUnit);
          }
        }
        if (!unitMatched) return false;
      }

      if (programs.length > 0) {
        const prog = item.program_operator || item.verifier_org || "";
        if (!programs.some((p) => prog.toLowerCase().includes(p.toLowerCase()))) return false;
      }

      const geo = (item.plant_geography || "").trim().toUpperCase();
      const firstTwo = geo.substring(0, 2);
      const codeToUse = (firstTwo.length === 2 && /^[A-Z]+$/.test(firstTwo)) ? firstTwo : geo;

      // Region Filter
      if (regions.length > 0) {
        const itemRegions = REGION_MAPPING[codeToUse] || [];
        const matched = regions.some((r) => itemRegions.includes(r));
        if (!matched) return false;
      }

      // Country Filter
      if (countries.length > 0) {
        if (!countries.includes(codeToUse)) return false;
      }

      if (standards.length > 0) {
        const pcrStr = (item.pcr || "").toLowerCase();
        const matched = standards.some((std) => {
          if (std === "EN 15804+A1") {
            return pcrStr.includes("15804:a1") || pcrStr.includes("15804+a1") || pcrStr.includes("15804:2012+a1") || pcrStr.includes("en 15804") || pcrStr.includes("nf en 15804");
          }
          if (std === "EN 15804+A2") {
            return pcrStr.includes("15804:a2") || pcrStr.includes("15804+a2") || pcrStr.includes("15804:2012+a2") || pcrStr.includes("en 15804") || pcrStr.includes("nf en 15804");
          }
          if (std === "ISO 14040 / 14044") {
            return pcrStr.includes("14040") || pcrStr.includes("14044");
          }
          if (std === "ISO 21930") {
            return pcrStr.includes("21930");
          }
          if (std === "ISO 14025") {
            return pcrStr.includes("14025");
          }
          return false;
        });
        if (!matched) return false;
      }

      if (years.length > 0) {
        const date = item.updated_on || item.created_on;
        if (!date) return false;
        const yr = new Date(date).getFullYear();
        if (!years.includes(yr)) return false;
      }

      return true;
    });

    setFilteredEPDs(filtered);
    setSelectedRows([]);
  }, [epds, appliedSnapshot]);

  // Extract unique years
  const availableYears = useMemo(() => {
    const years = epds
      .map((item) => {
        const date = item.updated_on || item.created_on;
        if (!date) return null;
        const yr = new Date(date).getFullYear();
        return isNaN(yr) ? null : yr;
      })
      .filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a);
  }, [epds]);

  const scrollToGrid = () => {
    setTimeout(() => {
      gridSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  const handleApplyFilters = () => {
    setAppliedSnapshot({
      search: searchQuery,
      manufacturers: selectedManufacturers,
      category: selectedCategory,
      subCategory: selectedSubCategory,
      unit: selectedUnit,
      programs: selectedPrograms,
      regions: selectedRegions,
      countries: selectedCountries,
      standards: selectedStandards,
      years: selectedYears,
    });
    scrollToGrid();
  };

  const handleManufacturerCardClick = (mName) => {
    setSelectedManufacturers([mName]);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setAppliedSnapshot({
      search: "", manufacturers: [mName], category: null, subCategory: null,
      unit: "", programs: [], regions: [], countries: [], standards: [], years: [],
    });
    scrollToGrid();
  };

  const handleCategoryCardClick = (cat) => {
    setSelectedCategory(cat);
    setSelectedSubCategory(null);
    setSelectedManufacturers([]);
    setAppliedSnapshot({
      search: "", manufacturers: [], category: cat, subCategory: null,
      unit: "", programs: [], regions: [], countries: [], standards: [], years: [],
    });
    scrollToGrid();
  };

  const resetAllFilters = () => {
    setSearchQuery("");
    setSelectedManufacturers([]);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedUnit("");
    setSelectedPrograms([]);
    setSelectedRegions([]);
    setSelectedCountries([]);
    setSelectedStandards([]);
    setSelectedYears([]);
    setAppliedSnapshot(null);
    // Fetch Concrete again on reset to populate filters
    Product_EPD.get_by_category("Concrete")
      .then((res) => res.json())
      .then((data) => setEPDs(data || []))
      .catch(() => setEPDs([]));
    setFilteredEPDs([]);
  };

  const getGwpValues = useCallback(() => {
    const dataSource = selectedRows.length > 0 ? selectedRows : filteredEPDs;
    return dataSource.map((item) => parseFloat(item.gwp)).filter((value) => !isNaN(value));
  }, [selectedRows, filteredEPDs]);

  useEffect(() => {
    setGwpValues(getGwpValues());
  }, [filteredEPDs, selectedRows, setGwpValues, getGwpValues]);

  const onSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    setSelectedRows(selectedData);
    
    const row = selectedData[0];
    if (row && epds && epds.length > 0) {
      // Helper to extract values from EPD JSON string
      const extractImpactValue = (jsonStr, indicatorKey) => {
        if (!jsonStr) return null;
        try {
          const data = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
          const impacts = data?.impacts || {};
          for (const method of Object.values(impacts)) {
            const keyToFind = indicatorKey === "ep_marine" ? "ep-marine" : indicatorKey;
            const indicator = method[indicatorKey] || method[keyToFind];
            if (indicator) {
              const phase = indicator.A1A2A3 || indicator.A1_A3 || indicator.A1a3 || indicator.total;
              if (phase && typeof phase.mean === "number") {
                return phase.mean;
              }
              for (const phaseVal of Object.values(indicator)) {
                if (phaseVal && typeof phaseVal.mean === "number") {
                  return phaseVal.mean;
                }
              }
            }
          }
        } catch (e) {
          // ignore parsing error
        }
        return null;
      };

      const siblingEPDs = epds.filter(e => e.category === row.category);
      
      const calculateAverage = (field) => {
        const vals = siblingEPDs.map(e => {
          const directVal = Number(e[field]);
          if (!isNaN(directVal) && directVal > 0) return directVal;
          return extractImpactValue(e.json, field);
        }).filter(v => v !== null && !isNaN(v) && v > 0);
        
        return vals.length > 0 ? vals.reduce((sum, v) => sum + v, 0) / vals.length : null;
      };

      const categoryAverages = {
        gwp: calculateAverage("gwp"),
        ap: calculateAverage("ap"),
        odp: calculateAverage("odp"),
        pocp: calculateAverage("pocp"),
        ep_marine: calculateAverage("ep_marine")
      };

      const ap = extractImpactValue(row.json, "ap") || Number(row.ap) || 0;
      const odp = extractImpactValue(row.json, "odp") || Number(row.odp) || 0;
      const pocp = extractImpactValue(row.json, "pocp") || Number(row.pocp) || 0;
      const ep_marine = extractImpactValue(row.json, "ep_marine") || Number(row.ep_marine) || 0;

      if (setFirstSelectedRow) {
        setFirstSelectedRow({
          ...row,
          ap,
          odp,
          pocp,
          ep_marine,
          categoryAverages
        });
      }
    } else {
      if (setFirstSelectedRow) setFirstSelectedRow(null);
    }
  };

  const gwpValues = getGwpValues();
  const gwpMin = gwpValues.length > 0 ? Math.min(...gwpValues) : null;
  const gwpMax = gwpValues.length > 0 ? Math.max(...gwpValues) : null;
  const gwpMean = gwpValues.length > 0 ? gwpValues.reduce((s, v) => s + v, 0) / gwpValues.length : null;
  const formatGwpValue = (val) => (val === null || !isFinite(val) ? "N/A" : val.toFixed(2));

  const handleAddToProductsClick = () => {
    if (selectedRows.length === 0) return;
    const initialQuantities = {};
    selectedRows.forEach((row) => { initialQuantities[row.id] = 1; });
    setQuantities(initialQuantities);
    setShowCalculatorModal(true);
  };

  const handleAddToProducts = async (currentQuantities = {}) => {
    if (selectedRows.length === 0) return;
    try {
      let successCount = 0;
      for (const selectedEPD of selectedRows) {
        const body = {
          name: selectedEPD.name,
          supplier: selectedEPD.manufacturer,
          leed_category: appliedSnapshot?.category?.key || selectedEPD.category || "",
          sub_category: appliedSnapshot?.subCategory?.key || selectedEPD.category || "",
          gwp: parseFloat(selectedEPD.gwp) || 0,
          year: new Date(selectedEPD.updated_on).getFullYear() || new Date().getFullYear(),
          quantity: parseFloat(currentQuantities[selectedEPD.id]) || 1,
          specifications: selectedEPD.description,
          is_validated: 1,
          functional_unit: "other",
          other_unit: selectedEPD.declared_unit,
          place_of_manufacture: selectedEPD.plant_geography,
          intro: selectedEPD.description,
          is_epd: 1,
        };
        const res = await Product.newEPD(body);
        if (res.ok) successCount++;
      }
      if (successCount > 0) {
        enqueueAlert({ message: t("Selected products added to workspace"), severity: "success" });
      }
    } catch (error) {
      console.error("Error adding to workspace:", error);
    }
  };

  const featuredManufacturers = useMemo(() => {
    return allManufacturersList.slice(0, 6);
  }, [allManufacturersList]);

  // Active filter chips list
  const activeChips = useMemo(() => {
    if (!appliedSnapshot) return [];
    const chips = [];

    appliedSnapshot.manufacturers.forEach((m) => {
      chips.push({ label: `${t("Mfg")}: ${m}`, type: "mfg", value: m });
    });
    if (appliedSnapshot.category) {
      chips.push({ label: `${t("Category")}: ${appliedSnapshot.category.label}`, type: "category" });
    }
    if (appliedSnapshot.subCategory) {
      chips.push({ label: `${t("Subcategory")}: ${appliedSnapshot.subCategory.label}`, type: "subcategory" });
    }
    if (appliedSnapshot.unit) {
      chips.push({ label: `${t("Unit")}: ${appliedSnapshot.unit}`, type: "unit" });
    }
    appliedSnapshot.regions.forEach((r) => {
      chips.push({ label: `${t("Region")}: ${r}`, type: "region", value: r });
    });
    appliedSnapshot.countries.forEach((c) => {
      const name = getCountryName(c);
      const flag = getFlagEmoji(c);
      chips.push({ label: `${t("Country")}: ${flag} ${name}`, type: "country", value: c });
    });
    appliedSnapshot.standards.forEach((s) => {
      chips.push({ label: `${t("Standard")}: ${s}`, type: "standard", value: s });
    });
    appliedSnapshot.years.forEach((y) => {
      chips.push({ label: `${t("Year")}: ${y}`, type: "year", value: y });
    });

    return chips;
  }, [appliedSnapshot, t]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* 2-Column Layout */}
      <Box sx={{ display: "flex", gap: 3, width: "100%", alignItems: "flex-start" }}>

        {/* ── Left: Advanced Filter Sidebar ── */}
        <EPDFiltersSidebar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          allManufacturers={allManufacturersList}
          selectedManufacturers={selectedManufacturers}
          setSelectedManufacturers={setSelectedManufacturers}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedSubCategory={selectedSubCategory}
          setSelectedSubCategory={setSelectedSubCategory}
          selectedUnit={selectedUnit}
          setSelectedUnit={setSelectedUnit}
          selectedPrograms={selectedPrograms}
          setSelectedPrograms={setSelectedPrograms}
          selectedRegions={selectedRegions}
          setSelectedRegions={setSelectedRegions}
          selectedCountries={selectedCountries}
          setSelectedCountries={setSelectedCountries}
          selectedStandards={selectedStandards}
          setSelectedStandards={setSelectedStandards}
          selectedYears={selectedYears}
          setSelectedYears={setSelectedYears}
          availableYears={availableYears}
          onResetAll={resetAllFilters}
          onApplyFilters={handleApplyFilters}
          epds={epds}
        />

        {/* ── Right: Scrollable Content (Home + Grid) ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>

          {/* ─────── HOME SECTION (Always visible) ─────── */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>

            {/* Search Bar */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                fullWidth
                placeholder={t("Search manufacturer, category, subcategory...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleApplyFilters(); }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      <LuSearch size={18} color={isDark ? "#94a3b8" : "#64748b"} />
                    </Box>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: ORG_PALETTE.font.family,
                    fontSize: "15px",
                    backgroundColor: tok.bg.card,
                    borderRadius: "10px",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  backgroundColor: isDark ? "#4ade80" : "#16a34a",
                  color: "#ffffff",
                  fontFamily: ORG_PALETTE.font.family,
                  fontWeight: 600,
                  fontSize: "15px",
                  textTransform: "none",
                  px: 4,
                  borderRadius: "10px",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: isDark ? "#22c55e" : "#15803d" },
                }}
              >
                {t("Search")}
              </Button>
            </Box>

            {/* ── Featured Manufacturers ── */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 700,
                    fontSize: "22px",
                    color: tok.text.primary,
                  }}>
                    {t("Manufacturers")}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => setShowAllManufacturersModal(true)}
                  endIcon={<LuChevronRight size={16} />}
                  sx={{
                    color: isDark ? "#4ade80" : "#16a34a",
                    textTransform: "none",
                    fontWeight: 600,
                    fontFamily: ORG_PALETTE.font.family,
                    fontSize: "14px",
                  }}
                >
                  {t("See all")}
                </Button>
              </Box>

              {allManufacturersList.length === 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2, color: isDark ? "#94a3b8" : "#64748b" }}>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "13px" }}>
                    {t("Loading manufacturers...")}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)" },
                  gap: 2,
                }}>
                  {featuredManufacturers.map((mItem, idx) => {
                    const name = typeof mItem === "string" ? mItem : mItem.manufacturer || "";
                    const count = typeof mItem === "object" ? mItem.count : null;
                    const isActive = appliedSnapshot?.manufacturers.includes(name);
                    return (
                      <Box
                        key={idx}
                        onClick={() => handleManufacturerCardClick(name)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          backgroundColor: isActive
                            ? (isDark ? "rgba(34,197,94,0.08)" : "#f0fdf4")
                            : tok.bg.card,
                          border: `1.5px solid ${isActive ? (isDark ? "#22c55e" : "#16a34a") : tok.border.card}`,
                          borderRadius: "12px",
                          cursor: "pointer",
                          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.15)" : "0 4px 10px rgba(0,0,0,0.02)",
                          "&:hover": {
                            borderColor: isDark ? "#4ade80" : "#22c55e",
                            transform: "translateY(-3px)",
                            boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.3)" : "0 8px 20px rgba(0,0,0,0.06)",
                          },
                        }}
                      >
                        <Box sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isActive
                            ? "linear-gradient(135deg, #22c55e 0%, #15803d 100%)"
                            : `linear-gradient(135deg, ${isDark ? "#3f3f46" : "#e2e8f0"} 0%, ${isDark ? "#27272a" : "#cbd5e1"} 100%)`,
                          color: isActive ? "#ffffff" : (isDark ? "#cbd5e1" : "#475569"),
                          fontWeight: 700,
                          fontSize: "18px",
                          fontFamily: "Poppins, sans-serif",
                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
                        }}>
                          {name.substring(0, 1).toUpperCase()}
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, minWidth: 0, flex: 1 }}>
                          <Typography noWrap sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: isActive ? (isDark ? "#4ade80" : "#15803d") : tok.text.primary,
                            lineHeight: 1.25,
                          }}>
                            {name}
                          </Typography>
                          {count && (
                            <Typography sx={{
                              fontFamily: ORG_PALETTE.font.family,
                              fontSize: "12px",
                              color: isDark ? "#94a3b8" : "#64748b",
                              fontWeight: 500,
                            }}>
                              {count} {t("EPDs")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* ── Product Categories ── */}
            <Box>
              <Typography sx={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 700,
                fontSize: "22px",
                color: tok.text.primary,
                mb: 2.5,
                textAlign: "left",
              }}>
                {t("Choose product category")}
              </Typography>

              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(4,1fr)", lg: "repeat(5,1fr)" },
                gap: 2.5,
              }}>
                {leeds.map((cat) => {
                  const colors = CATEGORY_COLORS[cat.key] || DEFAULT_COLORS;
                  const isActive = appliedSnapshot?.category?.key === cat.key;
                  const subTagsToShow = cat.sub_categories?.slice(0, 4) || [];
                  const remaining = (cat.sub_categories?.length || 0) - subTagsToShow.length;
                  const remainingSubsList = cat.sub_categories?.slice(4) || [];

                  const tooltipContent = (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start", width: "100%", textAlign: "left" }}>
                      {remainingSubsList.map((sub) => (
                        <Typography key={sub.key} sx={{ fontSize: "12px", fontFamily: "Poppins, sans-serif", color: "#ffffff !important", textAlign: "left", display: "block", width: "100%" }}>
                          · {sub.label}
                        </Typography>
                      ))}
                    </Box>
                  );

                  return (
                    <Box
                      key={cat.key}
                      onClick={() => handleCategoryCardClick(cat)}
                      sx={{
                        backgroundColor: tok.bg.card,
                        border: `2px solid ${isActive ? (isDark ? "#4ade80" : "#16a34a") : tok.border.card}`,
                        borderRadius: "16px",
                        overflow: "hidden",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        height: "230px", // Shorter height to eliminate excessive white space
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.2)" : "0 4px 12px rgba(0,0,0,0.03)",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.4)" : "0 12px 24px rgba(0,0,0,0.08)",
                          borderColor: isDark ? "#4ade80" : "#22c55e",
                        },
                      }}
                    >
                      {/* Colored Header Block with Title */}
                      {/* Premium 3D Rendered Cover Header with Title */}
                      <Box sx={{
                        height: "85px",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                        textAlign: "center",
                        backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.72) 0%, rgba(15, 23, 42, 0.82) 100%), url(${CATEGORY_IMAGES[cat.key]})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}>
                        <Typography sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 700,
                          fontSize: "15px",
                          color: "#ffffff",
                          lineHeight: 1.3,
                          textShadow: "0 2px 4px rgba(0,0,0,0.15)",
                          zIndex: 1,
                        }}>
                          {cat.label}
                        </Typography>
                        {isActive && (
                          <Box sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            zIndex: 2,
                          }}>
                            <Typography sx={{ color: colors.to, fontSize: "11px", fontWeight: 800 }}>✓</Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Card Content - Subcategories */}
                      <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          {subTagsToShow.map((sub) => (
                            <Typography key={sub.key} sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "13px",
                              fontWeight: 500,
                              color: isDark ? "#cbd5e1" : "#475569",
                              textAlign: "left",
                              lineHeight: 1.4,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                              · {sub.label}
                            </Typography>
                          ))}
                        </Box>

                        {remaining > 0 && (
                          <Tooltip
                            title={tooltipContent}
                            arrow
                            placement="top"
                            enterDelay={100}
                            leaveDelay={200}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: "#1e293b !important",
                                  color: "#ffffff !important",
                                  fontFamily: "Poppins, sans-serif !important",
                                  fontSize: "12.5px !important",
                                  boxShadow: "0 6px 20px rgba(0,0,0,0.18) !important",
                                  border: "1px solid rgba(255,255,255,0.08) !important",
                                  p: "12px !important",
                                  borderRadius: "8px !important",
                                  textAlign: "left !important",
                                }
                              },
                              arrow: {
                                sx: {
                                  color: "#1e293b !important",
                                }
                              }
                            }}
                            slotProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: "#1e293b !important",
                                  color: "#ffffff !important",
                                  fontFamily: "Poppins, sans-serif !important",
                                  fontSize: "12.5px !important",
                                  boxShadow: "0 6px 20px rgba(0,0,0,0.18) !important",
                                  border: "1px solid rgba(255,255,255,0.08) !important",
                                  p: "12px !important",
                                  borderRadius: "8px !important",
                                  textAlign: "left !important",
                                }
                              },
                              arrow: {
                                sx: {
                                  color: "#1e293b !important",
                                }
                              }
                            }}
                          >
                            <Box sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer", mt: 0.5 }}>
                              <Typography sx={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                color: isDark ? "#4ade80" : "#16a34a",
                                mr: 0.5,
                              }}>
                                +{remaining} {t("more")}
                              </Typography>
                              <LuChevronRight size={13} color={isDark ? "#4ade80" : "#16a34a"} />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box> {/* Closes 2-Column Layout Box */}

      {/* ─────── GRID SECTION (visible when appliedSnapshot is set, full-width) ─────── */}
      {appliedSnapshot && (
        <Box ref={gridSectionRef} sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%", mt: 4 }}>

          {/* Active Filter Chips (Outside and above the table container card) */}
          {activeChips.length > 0 && (
            <Box sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
              py: 1,
              width: "100%",
            }}>
              <Typography sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: isDark ? "#cbd5e1" : "#475569",
                fontFamily: "Poppins, sans-serif",
              }}>
                {t("Active Filters")}:
              </Typography>
              {activeChips.map((chip, cIdx) => {
                const handleDelete = () => {
                  if (chip.type === "mfg") {
                    const newMfg = selectedManufacturers.filter((x) => x !== chip.value);
                    setSelectedManufacturers(newMfg);
                    setAppliedSnapshot((prev) => ({ ...prev, manufacturers: newMfg }));
                  } else if (chip.type === "category") {
                    setSelectedCategory(null);
                    setSelectedSubCategory(null);
                    setAppliedSnapshot((prev) => ({ ...prev, category: null, subCategory: null }));
                  } else if (chip.type === "subcategory") {
                    setSelectedSubCategory(null);
                    setAppliedSnapshot((prev) => ({ ...prev, subCategory: null }));
                  } else if (chip.type === "unit") {
                    setSelectedUnit("");
                    setAppliedSnapshot((prev) => ({ ...prev, unit: "" }));
                  } else if (chip.type === "region") {
                    const newRegs = selectedRegions.filter((x) => x !== chip.value);
                    setSelectedRegions(newRegs);
                    setAppliedSnapshot((prev) => ({ ...prev, regions: newRegs }));
                  } else if (chip.type === "country") {
                    const newCountries = selectedCountries.filter((x) => x !== chip.value);
                    setSelectedCountries(newCountries);
                    setAppliedSnapshot((prev) => ({ ...prev, countries: newCountries }));
                  } else if (chip.type === "standard") {
                    const newStds = selectedStandards.filter((x) => x !== chip.value);
                    setSelectedStandards(newStds);
                    setAppliedSnapshot((prev) => ({ ...prev, standards: newStds }));
                  } else if (chip.type === "year") {
                    const newYears = selectedYears.filter((x) => x !== chip.value);
                    setSelectedYears(newYears);
                    setAppliedSnapshot((prev) => ({ ...prev, years: newYears }));
                  }
                };

                return (
                  <Box
                    key={cIdx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.06)",
                      border: `1.5px solid ${isDark ? "#22c55e" : "#16a34a"}`,
                      borderRadius: "20px",
                      paddingX: "14px",
                      paddingY: "5px",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "#ef4444",
                        backgroundColor: isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.04)",
                      }
                    }}
                  >
                    <Typography sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: isDark ? "#4ade80" : "#15803d",
                      fontFamily: "Poppins, sans-serif",
                      lineHeight: 1.2,
                    }}>
                      {chip.label}
                    </Typography>
                    <Box
                      onClick={handleDelete}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        color: isDark ? "#4ade80" : "#15803d",
                        transition: "color 0.2s",
                        "&:hover": {
                          color: "#ef4444",
                        }
                      }}
                    >
                      <LuX size={14} />
                    </Box>
                  </Box>
                );
              })}
              <Button
                size="small"
                onClick={resetAllFilters}
                sx={{
                  ml: "auto",
                  textTransform: "none",
                  fontSize: "14.5px",
                  color: isDark ? "#ef4444" : "#dc2626",
                  fontWeight: 700,
                  fontFamily: ORG_PALETTE.font.family,
                  "&:hover": {
                    textDecoration: "underline",
                    backgroundColor: isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.04)",
                  }
                }}
              >
                {t("Clear All")}
              </Button>
            </Box>
          )}

          {/* Metrics cards (Outside and above the table container card) */}
          {!isLoading && filteredEPDs.length > 0 && (
            <Box className="eng-flex-grid">
              <Box className="eng-metric-item">
                <MetricCard
                  title={selectedRows.length > 0 ? t("Selected EPDs") : t("Total EPDs")}
                  value={selectedRows.length > 0 ? selectedRows.length : filteredEPDs.length}
                  unit=""
                  accent="#6366f1"
                  bg={isDark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)"}
                  type="chain"
                  icon={LuFootprints}
                  delay={0.05}
                />
              </Box>
              <Box className="eng-metric-item">
                <MetricCard
                  title={t("Average GWP")}
                  value={gwpMean || 0}
                  unit="kg CO₂e"
                  accent="#2563eb"
                  bg={isDark ? "rgba(37,99,235,0.12)" : "rgba(37,99,235,0.08)"}
                  type="scope3"
                  icon={LuFootprints}
                  delay={0.12}
                />
              </Box>
              <Box className="eng-metric-item">
                <MetricCard
                  title={t("Min GWP")}
                  value={gwpMin || 0}
                  unit="kg CO₂e"
                  accent="#16a34a"
                  bg={isDark ? "rgba(22,163,74,0.12)" : "rgba(22,163,74,0.08)"}
                  type="cost"
                  icon={LuFootprints}
                  delay={0.19}
                />
              </Box>
              <Box className="eng-metric-item">
                <MetricCard
                  title={t("Max GWP")}
                  value={gwpMax || 0}
                  unit="kg CO₂e"
                  accent="#dc2626"
                  bg={isDark ? "rgba(220,38,38,0.12)" : "rgba(220,38,38,0.08)"}
                  type="scope1"
                  icon={LuFootprints}
                  delay={0.26}
                />
              </Box>
            </Box>
          )}

          {/* AgGrid Table Card */}
          <Paper
            variant="Table Container"
            sx={{ borderRadius: "12px", overflow: "hidden" }} // Removed p: 2.5 so grid goes edge-to-edge
          >
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">{t("EPD Library")}</Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                <ColumnVisibilityButton onClick={() => setShowColumnsModal((prev) => !prev)} />
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6, gap: 2 }}>
                <CircularProgress size={24} sx={{ color: isDark ? "#4ade80" : "#16a34a" }} />
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "14px", color: isDark ? "#94a3b8" : "#64748b" }}>
                  {t("Loading EPD data...")}
                </Typography>
              </Box>
            )}

            {/* No results */}
            {!isLoading && filteredEPDs.length === 0 && (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography sx={{
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "15px",
                  color: isDark ? "#94a3b8" : "#64748b",
                }}>
                  {t("No EPDs found for the selected filters.")}
                </Typography>
                <Button
                  size="small"
                  onClick={resetAllFilters}
                  sx={{ mt: 1.5, textTransform: "none", color: isDark ? "#4ade80" : "#16a34a", fontWeight: 600 }}
                >
                  {t("Reset filters")}
                </Button>
              </Box>
            )}

            {/* AgGrid Table */}
            {!isLoading && filteredEPDs.length > 0 && (
              <Box className="epd-info-table">
                <AgGrid
                  checkboxes={false}
                  headerCheckbox={false}
                  columnDefs={colDefs}
                  rowData={filteredEPDs}
                  onSelectionChanged={onSelectionChanged}
                  getRowId={(params) => String(params.data.id)}
                  colVisibility_key={colVisibility_key}
                  pagination={true}
                  maxHeight="540px"
                />
                <Box sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingY: 1.5,
                  paddingX: 3,
                  backgroundColor: isDark ? "#27272a" : "#f8fafc",
                  borderTop: `1px solid ${tok.border.divider}`,
                  width: "100%",
                  boxSizing: "border-box",
                  gap: 2,
                  flexWrap: "wrap",
                  borderRadius: "0 0 12px 12px",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CheckBox
                      checked={isBoxplotView}
                      onChange={() => { setIsBoxplotViewState(!isBoxplotView); setIsBoxplotView(!isBoxplotView); }}
                      label={t("Boxplot View")}
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "13px", color: isDark ? "#cbd5e1" : "#475569" }}>
                      {t("Min")}: <span style={{ fontWeight: 600 }}>{formatGwpValue(gwpMin)}</span> |{" "}
                      {t("Max")}: <span style={{ fontWeight: 600 }}>{formatGwpValue(gwpMax)}</span> |{" "}
                      {t("Mean")}: <span style={{ fontWeight: 600 }}>{formatGwpValue(gwpMean)}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      disabled={selectedRows.length < 2 || selectedRows.length > 4}
                      onClick={() => setShowCompareModal(true)}
                      sx={{ textTransform: "none", fontFamily: ORG_PALETTE.font.family, fontWeight: 600, borderRadius: "8px" }}
                    >
                      {t("Compare EPDs")} ({selectedRows.length})
                    </Button>
                    <Button
                      variant="contained"
                      disabled={selectedRows.length === 0}
                      onClick={handleAddToProductsClick}
                      sx={{
                        textTransform: "none",
                        fontFamily: ORG_PALETTE.font.family,
                        fontWeight: 600,
                        backgroundColor: isDark ? "#4ade80" : "#16a34a",
                        color: "#ffffff",
                        borderRadius: "8px",
                      }}
                    >
                      {t("Add to Product Workspace")}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={showColumnsModal}
        onClose={() => setShowColumnsModal(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />

      {/* Comparison Modal */}
      <Dialog open={showCompareModal} onClose={() => setShowCompareModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontFamily: ORG_PALETTE.font.family, fontWeight: 600 }}>{t("EPD Comparison")}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t("Attribute")}</TableCell>
                  {selectedRows.map((r) => (
                    <TableCell key={r.id} sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>{t("Manufacturer")}</TableCell>
                  {selectedRows.map((r) => (<TableCell key={r.id}>{r.manufacturer}</TableCell>))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>{t("GWP (kg CO₂e)")}</TableCell>
                  {selectedRows.map((r) => (<TableCell key={r.id} sx={{ fontWeight: 600 }}>{r.gwp}</TableCell>))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompareModal(false)} variant="contained">{t("Close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Calculator Modal */}
      <Dialog open={showCalculatorModal} onClose={() => setShowCalculatorModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: ORG_PALETTE.font.family, fontWeight: 600 }}>{t("EPD Quantity & Carbon Calculator")}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Material / EPD")}</TableCell>
                  <TableCell>{t("GWP Factor")}</TableCell>
                  <TableCell>{t("Quantity")}</TableCell>
                  <TableCell>{t("Embodied Carbon")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedRows.map((row) => {
                  const normGwp = parseFloat(row.normalized_gwp) || parseFloat(row.gwp) || 0;
                  const normUnit = row.normalized_unit || row.declared_unit || "unit";
                  const currentQty = quantities[row.id] || 0;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{normGwp.toFixed(2)} kg CO₂e / {normUnit}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={quantities[row.id] ?? ""}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setQuantities((prev) => ({ ...prev, [row.id]: isNaN(val) ? 0 : val }));
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{(currentQty * normGwp).toFixed(1)} kg CO₂e</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowCalculatorModal(false)}>{t("Cancel")}</Button>
          <Button
            onClick={() => { setShowCalculatorModal(false); handleAddToProducts(quantities); }}
            variant="contained"
            sx={{ backgroundColor: isDark ? "#4ade80" : "#16a34a", color: "#ffffff" }}
          >
            {t("Add to Workspace")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Manufacturers Modal */}
      <Dialog
        open={showAllManufacturersModal}
        onClose={() => setShowAllManufacturersModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: tok.bg.card,
            backgroundImage: "none",
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "20px", pb: 1 }}>
          {t("All Manufacturers")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("Search manufacturers...")}
            onChange={(e) => {
              const term = e.target.value.toLowerCase();
              setAllMfrSearchTerm(term);
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "14px",
                borderRadius: "8px",
              },
            }}
          />
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxHeight: "450px",
            overflowY: "auto",
            pr: 1,
          }}>
            {allManufacturersList
              .filter((m) => {
                const name = typeof m === "string" ? m : m.manufacturer || "";
                return name.toLowerCase().includes(allMfrSearchTerm);
              })
              .map((mItem, idx) => {
                const name = typeof mItem === "string" ? mItem : mItem.manufacturer || "";
                const count = typeof mItem === "object" ? mItem.count : null;
                const isActive = appliedSnapshot?.manufacturers.includes(name);
                return (
                  <Box
                    key={idx}
                    onClick={() => {
                      handleManufacturerCardClick(name);
                      setShowAllManufacturersModal(false);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${isActive ? (isDark ? "#4ade80" : "#16a34a") : tok.border.card}`,
                      backgroundColor: isActive ? (isDark ? "rgba(34,197,94,0.08)" : "#f0fdf4") : "transparent",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: isDark ? "#27272a" : "#f8fafc",
                        borderColor: isDark ? "#4ade80" : "#16a34a",
                      },
                    }}
                  >
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: isActive ? (isDark ? "#22c55e" : "#16a34a") : (isDark ? "#3f3f46" : "#e2e8f0"),
                      color: isActive ? "#fff" : (isDark ? "#cbd5e1" : "#475569"),
                      fontWeight: 700,
                      fontSize: "13px",
                    }}>
                      {name.substring(0, 1).toUpperCase()}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: isActive ? (isDark ? "#4ade80" : "#15803d") : tok.text.primary,
                      }}>
                        {name}
                      </Typography>
                      {count && (
                        <Typography sx={{ fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b" }}>
                          {count} {t("EPDs")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setShowAllManufacturersModal(false)} variant="contained" sx={{ backgroundColor: isDark ? "#4ade80" : "#16a34a", color: "#fff" }}>
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EPDTable;
